import { Grid, Typography } from '@mui/material';
import React from 'react';
import { CommunicationTrackingStatus } from '../../../../../hooks/useCommunicationTracking';
import { CommunicationTrackingEventType } from '../../../../../api';

type CommunicationTrackingSummaryProps = {
  status: CommunicationTrackingStatus;
  loading: boolean;
};
const getStatusFromEvents = (events: any[], type: 'email' | 'sms' | 'whatsapp') => {
  if (events.length === 0) return null;

  let failed = false;
  let highEngagement = false;
  let delivered = false;
  let sent = false;

  if (type === 'email') {
    failed = events.some(
      (e) =>
        e.event_type === CommunicationTrackingEventType.EMAIL_BOUNCE ||
        e.event_type === CommunicationTrackingEventType.EMAIL_DROPPED ||
        e.event_type === CommunicationTrackingEventType.EMAIL_SPAM_REPORT,
    );
    highEngagement = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_OPEN);
    delivered = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_DELIVERED);
    sent = events.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_PROCESSED);
  } else if (type === 'sms') {
    failed = events.some(
      (e) =>
        e.event_type === CommunicationTrackingEventType.SMS_FAILED ||
        e.event_type === CommunicationTrackingEventType.SMS_UNDELIVERED ||
        e.event_type === CommunicationTrackingEventType.SMS_WRONG_NUMBER ||
        e.event_type === CommunicationTrackingEventType.SMS_GENERIC_ERROR,
    );
    delivered = events.some((e) => e.event_type === CommunicationTrackingEventType.SMS_DELIVERED);
    sent = events.some(
      (e) =>
        e.event_type === CommunicationTrackingEventType.SMS_SENT ||
        e.event_type === CommunicationTrackingEventType.SMS_DISPATCHED,
    );
  } else if (type === 'whatsapp') {
    failed = events.some((e) => e.event_type === CommunicationTrackingEventType.WHATSAPP_FAILED);
    highEngagement = events.some((e) => e.event_type === CommunicationTrackingEventType.WHATSAPP_READ);
    delivered = events.some((e) => e.event_type === CommunicationTrackingEventType.WHATSAPP_DELIVERED);
    sent = events.some(
      (e) =>
        e.event_type === CommunicationTrackingEventType.WHATSAPP_SENT ||
        e.event_type === CommunicationTrackingEventType.WHATSAPP_DISPATCHED,
    );
  }

  if (failed) return { checkmarks: '✗', color: 'error.main' };
  if (highEngagement) return { checkmarks: '✓✓', color: 'primary.main' };
  if (delivered) return { checkmarks: '✓✓', color: 'text.secondary' };
  if (sent) return { checkmarks: '✓', color: 'text.secondary' };
  return null;
};
export const CommunicationTrackingSummary = ({ status, loading }: CommunicationTrackingSummaryProps) => {
  const getCommunicationStats = () => {
    if (loading || status.events.length === 0) {
      return null;
    }

    const emailEvents = status.events.filter((e) => e.event_type?.startsWith('email_'));
    const smsEvents = status.events.filter((e) => e.event_type?.startsWith('sms_'));
    const whatsappEvents = status.events.filter((e) => e.event_type?.startsWith('whatsapp_'));

    const emailIds = new Set(emailEvents.map((e) => e.sendgrid_email_data?.sg_message_id).filter(Boolean));
    const totalEmails = emailIds.size;

    // For SMS, show status if there are any SMS events (don't rely on message_sid count)
    const hasSmsEvents = smsEvents.length > 0;
    const hasWhatsappEvents = whatsappEvents.length > 0;

    if (totalEmails === 0 && !hasSmsEvents && !hasWhatsappEvents) {
      return null;
    }

    const emailStatus = getStatusFromEvents(emailEvents, 'email');
    const smsStatus = getStatusFromEvents(smsEvents, 'sms');
    const whatsappStatus = getStatusFromEvents(whatsappEvents, 'whatsapp');

    return { emailStatus, smsStatus, whatsappStatus, totalEmails, hasSmsEvents, hasWhatsappEvents };
  };

  const stats = getCommunicationStats();

  if (!stats) {
    return null;
  }

  return (
    <Grid container alignItems="center" gap={2}>
      {stats.emailStatus && stats.totalEmails > 0 && (
        <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto' }}>
          <Typography variant="body2">📧</Typography>
          <Typography variant="body2" sx={{ color: stats.emailStatus.color, fontWeight: 'bold', fontSize: '16px' }}>
            {stats.emailStatus.checkmarks}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats.totalEmails === 1 ? '1' : stats.totalEmails}
          </Typography>
        </Grid>
      )}

      {stats.smsStatus && stats.hasSmsEvents && (
        <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto' }}>
          <Typography variant="body2">📱</Typography>
          <Typography variant="body2" sx={{ color: stats.smsStatus.color, fontWeight: 'bold', fontSize: '16px' }}>
            {stats.smsStatus.checkmarks}
          </Typography>
        </Grid>
      )}

      {stats.whatsappStatus && stats.hasWhatsappEvents && (
        <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto' }}>
          <img src="/images/whatsapp.png" width="16" height="16" alt="WhatsApp" />
          <Typography variant="body2" sx={{ color: stats.whatsappStatus.color, fontWeight: 'bold', fontSize: '16px' }}>
            {stats.whatsappStatus.checkmarks}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
